import { LoadingOverlay, Modal, Space } from '@mantine/core';
import type { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { FormButtons } from '../../../components/form/FormButtons';
import { useModal } from '../../../hooks/useModal';
import { mutate } from '../../../util/api/mutate';
import { useUpsertAttendanceForm } from '../hooks/useUpsertAttendanceForm';
import type {
	AttendanceDto,
	UpsertAttendanceDto,
} from '../types/AttendanceDto';
import { UpsertAttendanceForm } from './UpsertAttendanceForm';
import { Title } from '../../../components/Title';

interface EditAttendanceModalProps {
	attendance: UpsertAttendanceDto;
	close: () => void;
	isOpened: boolean;
	updateTableValue: Dispatch<SetStateAction<AttendanceDto>>;
	refreshGroupTable: () => void;
}

export function EditAttendanceModal({
	attendance,
	isOpened,
	close,
	updateTableValue,
	refreshGroupTable,
}: EditAttendanceModalProps) {
	const { organisation } = useParams();

	const successModal = useModal();

	const { form, inputs } = useUpsertAttendanceForm(attendance);

	const editAttendanceMutation = mutate<UpsertAttendanceDto, AttendanceDto>(
		`${organisation}/attendance/edit`,
		inputs,
		'multipart/form-data',
	);

	const resetAndClose = () => {
		form.reset();
		close();
		successModal.close();
	};

	const complete = () => {
		if (editAttendanceMutation.data?.value) {
			updateTableValue(editAttendanceMutation.data.value);
			form.setInitialValues(inputs);
		}

		refreshGroupTable();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'Edit Attendance'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={editAttendanceMutation.isLoading} />
				<SmallModal
					isOpen={successModal.isOpen}
					close={complete}
					title={'Attendance Edited!'}
					body={'Attendance record has been edited. Click Ok to continue...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await editAttendanceMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								successModal.open();
							}
						});
					})}
				>
					<UpsertAttendanceForm
						form={form}
						selectedBranchId={inputs.branchId}
					/>
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
